import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import Seo from "../components/Seo";
import SectionEyebrow from "../components/SectionEyebrow";
import RevealOnScroll from "../components/RevealOnScroll";
import Button from "../components/Button";
import Lightbox from "../components/Lightbox";
import CaseStudyHero from "../components/CaseStudyHero";
import { WORKS } from "../data/content";
import { breadcrumbSchema } from "../lib/schemas";

const SchedaRow = ({ label, children }) => (
  <div className="grid grid-cols-12 gap-3 py-3 border-b border-current/15 text-[14px] leading-[1.55]">
    <dt className="col-span-5 md:col-span-4 font-condensed uppercase tracking-widest text-[12px] md:text-[13px] opacity-70">
      {label}
    </dt>
    <dd className="col-span-7 md:col-span-8">{children}</dd>
  </div>
);

const ApproccioBlock = ({ num, title, body }) => (
  <div className="border border-galactico/15 p-7 md:p-9 bg-galactico/[0.03]">
    <SectionEyebrow num={num}>{title}</SectionEyebrow>
    <p className="mt-5 text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[48ch]">
      {body}
    </p>
  </div>
);

const KpiBlock = ({ value, label }) => (
  <div className="border-t border-galactico/25 pt-6">
    <div
      className="font-display leading-[0.92] tracking-tight text-galactico"
      style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}
    >
      {value}
    </div>
    <div className="font-condensed uppercase tracking-widest text-[12px] md:text-[13px] mt-4 opacity-85 max-w-[24ch] leading-[1.35]">
      {label}
    </div>
  </div>
);

// HERO full-bleed (Sezione 2). Slot unico — l'utente la usa come "effetto wow".
const HERO_IMAGE =
  "/images/assets/l4g2rl7a_lg1a8605-scaled-1.jpg";

// SEZ. 5 — Il prodotto: 4 immagini fisse 2×2, mapping 1:1 con label.
const PRODOTTO_PHOTOS = [
  {
    src: "/images/assets/3duh4w51_lg1a8628-980x1470.jpg",
    label: "01 · Presa",
    alt: "Protek — Dettaglio polsino e palmo, costruzione con guarnizioni rosse",
  },
  {
    src: "/images/assets/vim891om_lg1a8617-980x1470.jpg",
    label: "02 · Comfort",
    alt: "Protek — Dettaglio etichetta laterale logo Protek su polsino",
  },
  {
    src: "/images/assets/k1n5ea7u_lg1a8635-980x1470.jpg",
    label: "03 · Durabilità",
    alt: "Protek — Dorso del guanto con grip in gomma sagomato e logo",
  },
  {
    src: "/images/assets/4dqzpatm_lg1a8650-980x1470.jpg",
    label: "04 · Stabilità",
    alt: "Protek — Dettaglio costruttivo rinforzo rosso ad alto contrasto",
  },
];

// Screenshot e-commerce (Sezione 7 — L'E-COMMERCE).
const ECOMMERCE_SCREENSHOT =
  "/images/assets/0zudeqcl_screenshot-2026-05-22-alle-20.14.50.png";

// Carosello modelli (Sezione 8 — I MODELLI). 5 slot, mapping 1:1.
// Anti-duplicazione: niente riuso di HERO, PRODOTTO_PHOTOS o ECOMMERCE come fallback.
const MODEL_SLOTS = 5;
const MODEL_PHOTOS = [
  // model-01 — Frame-6 · Colorway rosa/rosso ("Pink Crush")
  "/images/assets/b5bbx4l2_frame-6.jpg",
  // model-02 — Frame-4 · Colorway nero/verde acqua/giallo con palmo graphic ("Tropic Flow")
  "/images/assets/ec8e6qm3_frame-4.jpg",
  // model-03 — Frame-2 · Colorway nero/blu/rosso/bianco con palmo geometrico ("Tricolore")
  "/images/assets/rlsffukl_frame-2.jpg",
  // model-04 — Frame-5 · Colorway nero/blu/rosa-rosso ("Sunset")
  "/images/assets/i25fbsdo_frame-5-1.jpg",
  // model-05 — Frame-8 · Colorway nero/rosso ("Blackout Red")
  "/images/assets/4z9r337j_frame-8.jpg",
];

// SEZ. 7 — L'E-COMMERCE (sostituisce la vecchia "Galleria modelli" magazine
// che era basata su slot 06.jpg..12.jpg. Quei file non sono più referenziati).

const WorkProtek = () => {
  // Lightbox: solo le 4 foto della sezione "Il Prodotto".
  const ALL_LIGHTBOX = PRODOTTO_PHOTOS.map((p) => ({ src: p.src, alt: p.alt }));
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const carouselRef = useRef(null);

  const scrollCarousel = (direction) => {
    const el = carouselRef.current;
    if (!el) return;
    const delta = el.clientWidth * 0.8 * (direction === "right" ? 1 : -1);
    el.scrollBy({ left: delta, behavior: "smooth" });
  };

  useEffect(() => {
    if (lightboxIndex !== null) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [lightboxIndex]);

  const idx = WORKS.findIndex((w) => w.to === "/works/protek");
  const prev = idx > 0 ? WORKS[idx - 1] : WORKS[WORKS.length - 1];
  const next = idx >= 0 ? WORKS[(idx + 1) % WORKS.length] : WORKS[0];

  const breadcrumbJsonLd = breadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "Works", url: "/works" },
    { name: "Protek", url: "/works/protek" },
  ]);

  return (
    <>
      <Seo
        title="Protek: 1.500 paia in 6 mesi e +262% ROAS | OnSports Case"
        description="Performance marketing Protek (calze sportive): 1.500+ paia vendute in 6 mesi, +262% ROAS. Meta Ads e Google Ads gestiti da OnSports. 2025."
        path="/works/protek"
        image="/images/assets/l4g2rl7a_lg1a8605-scaled-1.jpg"
        jsonLd={breadcrumbJsonLd}
      />

      {/* SEZ. 1 — HERO */}
      <CaseStudyHero
        imagePath="/images/works/protek/hero.jpg"
        imageAlt="Protek · Goalkeeper Gloves · OnSports Brand"
        eyebrow="● Case study · Business Impact · Brand Creation · E-commerce"
        titleLines={["L'abbiamo immaginato. L'abbiamo costruito.", "L'abbiamo VENDUTO."]}
        highlightWord="VENDUTO."
        subtitle="Protek è l'unico caso di questo portfolio in cui OnSports è anche il brand owner. Lo abbiamo progettato, prodotto, fotografato e lanciato sull'e-commerce gestendo direttamente le campagne advertising. Oltre 1.500 paia vendute nei primi sei mesi con ROAS +262%. È il modo in cui dimostriamo che i framework che proponiamo ai clienti, li applichiamo prima di tutto a noi stessi."
        meta="Brand owner — OnSports  ·  Categoria — DTC Sportswear  ·  End-to-end brand creation"
      />

      {/* SEZ. 2 — HERO IMAGE FULL-BLEED */}
      <section className="bg-dodgers" data-testid="section-hero-image">
        <RevealOnScroll>
          {HERO_IMAGE ? (
            <img
              src={HERO_IMAGE}
              alt="Protek — Guanto da portiere, dettaglio polsino con logo brand su fondo nero"
              className="block w-screen object-cover"
              style={{ height: "clamp(45vh, 65vh, 75vh)", objectPosition: "center" }}
              loading="lazy"
              draggable="false"
              data-testid="hero-image-full"
            />
          ) : (
            <div
              className="w-screen bg-dodgers border-y border-lime/30 flex items-center justify-center"
              style={{ height: "clamp(45vh, 65vh, 75vh)" }}
              data-testid="hero-image-placeholder"
            >
              <span className="font-condensed uppercase tracking-widest text-[14px] text-galactico">
                Hero image in arrivo
              </span>
            </div>
          )}
        </RevealOnScroll>
        <div className="container-os pt-4 pb-12 md:pb-16">
          <p
            className="text-[13px] leading-[1.55] text-mandrogni opacity-80"
            style={{ fontStyle: "italic" }}
          >
            Feel Protekted. Il claim del brand, e l'unica cosa che vorresti
            sentire quando esci dai pali.
          </p>
        </div>
      </section>

      {/* SEZ. 3 — OVERVIEW */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-overview">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-7">
              <RevealOnScroll>
                <SectionEyebrow>Overview</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Il progetto.</h2>
              </RevealOnScroll>
              <div className="mt-8 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[760px]">
                <RevealOnScroll delay={0.15}>
                  <p>
                    Una consultancy che vende strategia di sponsorship, content
                    production e performance marketing ha un problema
                    strutturale: parla di prodotti che ha costruito per altri,
                    sempre come terza parte. Quando proponiamo a un brand di
                    lanciare un DTC sportivo, di prezzare correttamente un
                    prodotto, di costruire una sales page o di gestire campagne
                    Meta con ROAS misurabile, lo facciamo basandoci sul lavoro
                    fatto per i clienti. Protek nasce per testare i nostri
                    framework su un brand nostro.
                  </p>
                </RevealOnScroll>
                <RevealOnScroll delay={0.2}>
                  <p>
                    Protek è una linea di guanti da portiere di calcio,
                    progettata per offrire performance professionali a un
                    prezzo accessibile sia ai portieri di Serie A che a chi
                    gioca la domenica nel campionato dilettanti. Abbiamo
                    testato decine di modelli sul mercato per identificare le
                    caratteristiche tecniche che davvero fanno la differenza —
                    palmo, presa, durabilità del lattice, vestibilità del
                    polsino — e abbiamo costruito un guanto attorno a quei
                    parametri. Il claim del brand è Feel Protekted. Il
                    sub-claim, che ne sintetizza la value proposition, è Made
                    for everyday keepers.
                  </p>
                </RevealOnScroll>
                <RevealOnScroll delay={0.25}>
                  <p>
                    Abbiamo gestito ogni fase end-to-end: ideazione del brand,
                    design del prodotto, sviluppo del manufacturing con un
                    partner produttivo, photo shooting professionale, sviluppo
                    dell'e-commerce, lancio commerciale, gestione delle
                    campagne advertising. Dopo sei mesi dal lancio, Protek
                    aveva venduto oltre 1.500 paia con un ROAS sulle campagne
                    advertising del +262%.
                  </p>
                </RevealOnScroll>
              </div>
            </div>
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll delay={0.25}>
                <div className="border border-dodgers/20 p-6 md:p-7" data-testid="scheda-progetto">
                  <span className="font-condensed uppercase tracking-widest text-xs opacity-60 block mb-5">
                    Scheda progetto
                  </span>
                  <dl>
                    <SchedaRow label="Brand">Protek</SchedaRow>
                    <SchedaRow label="Prodotto">
                      Guanti da portiere · Linea pro + amateur
                    </SchedaRow>
                    <SchedaRow label="Brand owner">OnSports</SchedaRow>
                    <SchedaRow label="Target">
                      Everyday Keepers — dal pro al dilettante
                    </SchedaRow>
                    <SchedaRow label="Claim">Feel Protekted</SchedaRow>
                    <SchedaRow label="Sub-claim">
                      Made for everyday keepers
                    </SchedaRow>
                    <SchedaRow label="Ruolo">
                      Brand creation · Product design<br />
                      Manufacturing · Photo shooting<br />
                      E-commerce · Campagne advertising
                    </SchedaRow>
                    <SchedaRow label="Pilastro">
                      Business Impact · Brand &amp; Product · Performance
                    </SchedaRow>
                    <SchedaRow label="Anno">In-house project</SchedaRow>
                  </dl>
                </div>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 4 — LA SFIDA */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-sfida">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll>
                <SectionEyebrow>La sfida</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Costruire un brand DTC sul mercato dei guanti da portiere.
                </h2>
              </RevealOnScroll>
            </div>
            <div className="col-span-12 md:col-span-7 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[64ch] opacity-90">
              <RevealOnScroll delay={0.15}>
                <p>
                  Il mercato dei guanti da portiere è un segmento verticale
                  dominato da grandi brand sportswear globali (Adidas, Nike,
                  Reusch, Uhlsport, Sells), con prezzi medi tra i 60 e i 250
                  euro per i modelli di gamma alta e una scarsa offerta
                  strutturata sulla fascia intermedia — quella in cui si trova
                  la stragrande maggioranza dei portieri amatoriali italiani,
                  che sono il vero volume di mercato. La domanda esisteva, ma
                  era servita male: prodotti troppo cari per la maggior parte
                  degli utenti, oppure prodotti economici di qualità
                  insufficiente.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.22}>
                <p>
                  La sfida non era inventare un nuovo modello tecnico, ma
                  costruire un brand che parlasse il linguaggio dei portieri
                  amatoriali italiani — quelli che giocano nelle leghe
                  dilettantistiche, nei campionati a 7 e a 11, e che vogliono
                  un guanto serio senza dover spendere lo stipendio di un mese.
                  Tradotto in operativo: identificare le specifiche tecniche
                  minime non negoziabili, sviluppare un prodotto con margine
                  commerciale sostenibile, costruire un brand riconoscibile in
                  un mercato saturato visivamente, e creare la macchina di
                  vendita capace di portare il prodotto al consumatore finale
                  senza il filtro della distribuzione retail.
                </p>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 5 — IL PRODOTTO */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-prodotto">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10 md:mb-14">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll>
                <SectionEyebrow>Il prodotto</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Quattro caratteristiche, una sola promessa.
                </h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[68ch] opacity-90">
                  Abbiamo costruito i guanti Protek attorno a quattro qualità
                  tecniche misurabili: presa, comfort, durabilità del lattice
                  del palmo, stabilità del polsino. Ogni caratteristica è stata
                  testata in produzione attraverso prototipi successivi, fino a
                  raggiungere il rapporto qualità-prezzo che voleva il brand.
                </p>
              </RevealOnScroll>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="prodotto-grid">
            {PRODOTTO_PHOTOS.map((p, i) => (
              <RevealOnScroll key={i} delay={i * 0.05}>
                <button
                  type="button"
                  onClick={() => setLightboxIndex(i)}
                  className="block w-full text-left focus:outline-none group"
                  data-testid={`prodotto-${i}`}
                  data-hover="true"
                  aria-label={`Apri ${p.label}`}
                >
                  <div
                    className="block w-full overflow-hidden relative bg-dodgers/[0.04]"
                    style={{ aspectRatio: "4 / 5" }}
                  >
                    <img
                      src={p.src}
                      alt={p.alt}
                      loading="lazy"
                      className="block w-full h-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.02]"
                      draggable="false"
                    />
                    <span
                      className="absolute inset-0 pointer-events-none border-[1.5px] border-lime opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-hidden="true"
                    />
                  </div>
                  <span className="block mt-3 font-condensed uppercase tracking-widest text-lime text-[11px]">
                    {p.label}
                  </span>
                </button>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* SEZ. 6 — APPROCCIO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-approccio">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll>
                <SectionEyebrow>Approccio</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Quattro filiere. Una sola regia.
                </h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[68ch] opacity-90">
                  Costruire un brand DTC end-to-end significa tenere insieme
                  filiere normalmente gestite da agenzie diverse: branding,
                  product, e-commerce, performance. Protek è stato per noi
                  anche un esercizio di integrazione interna — applicare il
                  modello multi-disciplinare che proponiamo ai clienti a un
                  progetto in cui eravamo contemporaneamente strategist,
                  designer, retailer e media buyer.
                </p>
              </RevealOnScroll>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-7">
            <RevealOnScroll delay={0.05}>
              <ApproccioBlock
                num="01"
                title="Brand creation"
                body="Ideazione del nome Protek, sviluppo della brand identity (logo, palette, tipografia, tone of voice), costruzione del posizionamento Made for everyday keepers e del claim Feel Protekted. Definizione del territorio narrativo che differenzia il brand dai competitor globali e parla al pubblico amatoriale italiano."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.12}>
              <ApproccioBlock
                num="02"
                title="Product design & manufacturing"
                body="Analisi tecnica dei modelli concorrenti, identificazione delle caratteristiche tecniche non negoziabili, sviluppo prototipi successivi in collaborazione con il partner produttivo, validazione del prodotto finale su campioni reali di portieri. Definizione del pricing in funzione di margine commerciale sostenibile sul canale DTC."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.19}>
              <ApproccioBlock
                num="03"
                title="Photo shooting & visual assets"
                body="Photo shooting professionale del prodotto e dei testimonial, costruzione del catalogo visivo per e-commerce e social, sviluppo dei creative pack per le campagne advertising. Visual identity coerente tra prodotto, sito, social e materiali ADV."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.26}>
              <ApproccioBlock
                num="04"
                title="E-commerce & performance"
                body="Sviluppo dell'e-commerce Protek, integrazione del checkout, gestione del catalogo prodotti. Lancio delle campagne advertising su Meta (prospecting e retargeting), ottimizzazione progressiva delle audience e dei creative. Tracking dell'intero funnel dalla awareness alla conversione."
              />
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* SEZ. 7 — L'E-COMMERCE */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-ecommerce">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10 md:mb-14">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll>
                <SectionEyebrow>L'e-commerce</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Il sito che ha generato 1.500 paia di vendite.
                </h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[720px] opacity-90">
                  L'e-commerce Protek è stato progettato per parlare il
                  linguaggio dei portieri italiani: navigazione semplice, sales
                  page costruite attorno alle quattro caratteristiche tecniche
                  del prodotto, checkout ottimizzato per conversione mobile,
                  integrazione con Meta Ads per il retargeting. È il punto di
                  arrivo di tutto il funnel del brand.
                </p>
              </RevealOnScroll>
            </div>
          </div>

          <RevealOnScroll delay={0.05}>
            <div
              className="relative mx-auto max-w-5xl shadow-2xl overflow-hidden bg-galactico border border-mandrogni/20"
              data-testid="ecommerce-browser"
            >
              {/* Browser chrome */}
              <div className="flex items-center gap-2 h-9 md:h-10 px-3 md:px-4 bg-mandrogni/15 border-b border-mandrogni/20">
                <div className="flex gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-red-400" />
                  <span className="w-3 h-3 rounded-full bg-yellow-400" />
                  <span className="w-3 h-3 rounded-full bg-green-400" />
                </div>
                <div className="flex-1 mx-3 md:mx-4 h-6 bg-galactico flex items-center justify-center text-[11px] md:text-xs text-mandrogni font-mono">
                  protek-gloves.com
                </div>
              </div>
              {/* Screenshot */}
              {ECOMMERCE_SCREENSHOT ? (
                <img
                  src={ECOMMERCE_SCREENSHOT}
                  alt="E-commerce Protek — screenshot della homepage"
                  className="w-full h-auto block"
                  loading="lazy"
                  draggable="false"
                  data-testid="ecommerce-screenshot"
                />
              ) : (
                <div
                  className="w-full bg-dodgers flex items-center justify-center"
                  style={{ aspectRatio: "16 / 10" }}
                  data-testid="ecommerce-placeholder"
                >
                  <span className="font-condensed uppercase tracking-widest text-[14px] text-galactico">
                    Screenshot e-commerce in arrivo
                  </span>
                </div>
              )}
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 8 — I MODELLI · CAROSELLO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-modelli">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10 md:mb-14">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll>
                <SectionEyebrow>I modelli</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Cinque guanti. Una sola promessa.
                </h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[720px] opacity-90">
                  Cinque modelli che coprono tutte le esigenze del portiere —
                  dal pro che gioca in palazzetto agli amatori della domenica
                  all'aperto. Stesso DNA tecnico, vestibilità e palmi calibrati
                  per livello di gioco e superficie.
                </p>
              </RevealOnScroll>
            </div>
          </div>

          <div className="relative">
            <div
              ref={carouselRef}
              className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              data-testid="modelli-carosello"
            >
              {Array.from({ length: MODEL_SLOTS }).map((_, i) => {
                const photo = MODEL_PHOTOS[i];
                const slotClass =
                  "flex-none w-full md:w-1/2 lg:w-1/3 snap-start aspect-square overflow-hidden";

                if (!photo) {
                  return (
                    <div
                      key={i}
                      className={`${slotClass} bg-dodgers/[0.05] border border-lime border-dashed flex items-center justify-center`}
                      data-testid={`modello-placeholder-${i}`}
                      aria-label={`Modello ${i + 1} di ${MODEL_SLOTS} — in arrivo`}
                    >
                      <span
                        className="font-condensed uppercase tracking-widest text-galactico"
                        style={{ fontSize: "12px" }}
                      >
                        Modello in arrivo
                      </span>
                    </div>
                  );
                }

                return (
                  <div
                    key={i}
                    className={`${slotClass} bg-mandrogni/10`}
                    data-testid={`modello-${i}`}
                  >
                    <img
                      src={photo}
                      alt={`Protek Model 0${i + 1}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      draggable="false"
                    />
                  </div>
                );
              })}
            </div>

            {/* Frecce navigazione (desktop only) */}
            <div className="hidden md:flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => scrollCarousel("left")}
                className="w-10 h-10 border border-galactico/30 flex items-center justify-center text-galactico hover:bg-lime hover:text-dodgers hover:border-lime transition"
                aria-label="Modello precedente"
                data-testid="modelli-prev"
                data-hover="true"
              >
                <ArrowLeft size={18} />
              </button>
              <button
                type="button"
                onClick={() => scrollCarousel("right")}
                className="w-10 h-10 border border-galactico/30 flex items-center justify-center text-galactico hover:bg-lime hover:text-dodgers hover:border-lime transition"
                aria-label="Modello successivo"
                data-testid="modelli-next"
                data-hover="true"
              >
                <ArrowUpRight size={18} className="rotate-45" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <Lightbox
        images={ALL_LIGHTBOX}
        openIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
      />

      {/* SEZ. 8 — IMPATTO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-impatto">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12 md:mb-16">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll>
                <SectionEyebrow>Impatto</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  I numeri che pochi consulenti possono mostrarti.
                </h2>
              </RevealOnScroll>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
            <RevealOnScroll delay={0.05}>
              <KpiBlock value="1.500+" label="Paia vendute nei primi sei mesi dal lancio" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.1}>
              <KpiBlock value="+262%" label="ROAS sulle campagne Meta advertising" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.15}>
              <KpiBlock value="6" label="Mesi dal lancio al risultato commerciale" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.2}>
              <KpiBlock value="1" label="Brand creato da zero end-to-end" />
            </RevealOnScroll>
          </div>

          <p className="mt-8 text-[13px] leading-[1.55] text-mandrogni opacity-80 max-w-[80ch]">
            Dati commerciali e di campagna Protek nei primi sei mesi dal lancio
            dell'e-commerce. Brand di proprietà OnSports, progettato e venduto
            direttamente, senza distribuzione retail intermedia.
          </p>

          <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            <RevealOnScroll delay={0.05}>
              <div>
                <h4 className="font-condensed uppercase tracking-widest text-sm md:text-[15px] mb-3 text-lime">
                  Numeri di business, non di vanità
                </h4>
                <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[40ch]">
                  ROAS e paia vendute sono metriche di conversione, non di
                  awareness. Sono i numeri che un CFO chiede per primi e che il
                  marketing sportivo italiano fatica spesso a produrre. Per noi
                  sono stati la dimostrazione che il modello DTC sport-consumer
                  funziona anche su scala piccola, se costruito con disciplina
                  dall'inizio.
                </p>
              </div>
            </RevealOnScroll>
            <RevealOnScroll delay={0.12}>
              <div>
                <h4 className="font-condensed uppercase tracking-widest text-sm md:text-[15px] mb-3 text-lime">
                  Un brand vero, non un esperimento
                </h4>
                <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[40ch]">
                  Protek non è un side-project di studio, è un brand attivo sul
                  mercato. Il fatto che sia di proprietà OnSports non è un
                  dettaglio narrativo: significa che abbiamo preso rischio
                  commerciale, abbiamo investito capitale, e abbiamo costruito
                  un'attività con sell-through reale. È il modo in cui
                  dimostriamo che il modello platform-content-performance che
                  proponiamo ai clienti regge il test del mercato reale.
                </p>
              </div>
            </RevealOnScroll>
            <RevealOnScroll delay={0.19}>
              <div>
                <h4 className="font-condensed uppercase tracking-widest text-sm md:text-[15px] mb-3 text-lime">
                  Da consultancy a brand owner
                </h4>
                <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[40ch]">
                  Il portfolio OnSports è fatto al 90% di progetti per clienti.
                  Protek occupa il 10% restante per ragioni strategiche: ci
                  permette di dire ai clienti che i framework che proponiamo
                  non li abbiamo solo immaginati — li abbiamo applicati sui
                  nostri soldi. È la differenza tra una consulenza che parla in
                  teoria e una consultancy che ha provato il proprio modello
                  sul campo.
                </p>
              </div>
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* SEZ. 9 — CREDITS */}
      <section className="py-20 md:py-28 bg-galactico text-dodgers" data-testid="section-credits">
        <div className="container-os">
          <RevealOnScroll>
            <SectionEyebrow>Credits</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <div className="mt-8 max-w-[720px]">
              <dl>
                <SchedaRow label="Brand">Protek</SchedaRow>
                <SchedaRow label="Brand owner">OnSports</SchedaRow>
                <SchedaRow label="Prodotto">
                  Guanti da portiere · Linea pro + amateur
                </SchedaRow>
                <SchedaRow label="Ruolo OnSports">
                  Brand creation · Product design<br />
                  Manufacturing · Photo shooting<br />
                  E-commerce sviluppo &amp; gestione<br />
                  Campagne Meta Ads · Performance marketing
                </SchedaRow>
                <SchedaRow label="Servizi dimostrati">
                  Brand &amp; Product Strategy · Content &amp; IP Studio<br />
                  Performance Marketing &amp; Growth
                </SchedaRow>
                <SchedaRow label="Tipologia">
                  Caso in-house · Progetto end-to-end<br />
                  con brand di proprietà OnSports
                </SchedaRow>
              </dl>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 10 — CTA FINALE */}
      <section
        className="bg-dodgers text-galactico py-24 md:py-40 border-t border-galactico/10"
        data-testid="section-cta-finale"
      >
        <div className="container-os">
          <RevealOnScroll>
            <h2 className="display-h2 max-w-[30ch]">
              Hai un'idea di prodotto sport-consumer che vorresti vedere sul
              mercato? Costruiamo insieme il suo{" "}
              <span className="text-lime">IMPATTO</span>.
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <p className="mt-8 body-lg opacity-85 max-w-[62ch]">
              Quello che abbiamo fatto per Protek possiamo farlo con te, con il
              tuo brand, con il tuo prodotto. Brand creation, product design,
              e-commerce, performance marketing. La filiera completa, sotto
              un'unica regia, con KPI commerciali tracciabili sin dal primo
              giorno.
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.18}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contatti" data-testid="cta-parliamone">
                <Button variant="primary">Parliamone</Button>
              </Link>
              <Link to="/works" data-testid="cta-altri-casi">
                <Button variant="secondary-light">Vedi altri casi studio</Button>
              </Link>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 11 — NAVIGAZIONE PROGETTI */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-nav-progetti">
        <div className="container-os">
          <RevealOnScroll>
            <SectionEyebrow>Naviga i progetti</SectionEyebrow>
          </RevealOnScroll>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link
              to={prev.to || "/works"}
              className="group border border-dodgers/20 p-7 md:p-10 hover:border-dodgers transition-colors flex flex-col gap-4"
              data-hover="true"
              data-testid="nav-prev"
            >
              <span className="font-condensed uppercase tracking-widest text-xs opacity-70">
                ← Progetto precedente
              </span>
              <span className="font-display text-2xl md:text-3xl leading-tight group-hover:text-mandrogni transition-colors">
                {prev.title}
              </span>
              <span className="text-sm opacity-75">{prev.category}</span>
            </Link>
            <Link
              to={next.to || "/works"}
              className="group border border-dodgers/20 p-7 md:p-10 hover:border-dodgers transition-colors flex flex-col gap-4 md:text-right md:items-end"
              data-hover="true"
              data-testid="nav-next"
            >
              <span className="font-condensed uppercase tracking-widest text-xs opacity-70 inline-flex items-center gap-2">
                Progetto successivo <ArrowUpRight size={14} />
              </span>
              <span className="font-display text-2xl md:text-3xl leading-tight group-hover:text-mandrogni transition-colors">
                {next.title}
              </span>
              <span className="text-sm opacity-75">{next.category}</span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default WorkProtek;
